import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Copy, LogIn, Loader2, Power, PowerOff, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Super Admin > Sales & Revenue > Sales Reps
 * List + invite + impersonate.
 */
export const SalesRepsManagementTab = () => {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newRep, setNewRep] = useState({ email: '', name: '', territory: '' });
  const [submitting, setSubmitting] = useState(false);

  const H = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/superadmin/reps`, H());
      setReps(r.data.reps || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reps');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!newRep.email || !newRep.name) return toast.error('Email and name required');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/superadmin/reps/invite`, newRep, H());
      const url = r.data.rep.setup_url;
      await navigator.clipboard?.writeText(url);
      toast.success('Rep invited — setup link copied to clipboard');
      setNewRep({ email: '', name: '', territory: '' });
      setShowNew(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invite failed');
    }
    setSubmitting(false);
  };

  const impersonate = async (rep) => {
    if (!window.confirm(`Impersonate ${rep.name}? Opens their portal in a new tab as them.`)) return;
    try {
      const r = await axios.post(`${API}/api/superadmin/reps/${rep.user_id}/impersonate`, {}, H());
      // Open new tab with token in URL — RepDashboard swaps it into localStorage
      window.open(`/rep-dashboard?impersonate=${encodeURIComponent(r.data.session_token)}`, '_blank');
      toast.success(`Opened ${rep.name}'s portal in a new tab.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impersonate failed');
    }
  };

  const toggleActive = async (rep) => {
    try {
      await axios.patch(`${API}/api/superadmin/reps/${rep.user_id}`, { is_active: !rep.is_active }, H());
      toast.success(`Rep ${!rep.is_active ? 'activated' : 'deactivated'}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="sales-reps-mgmt-tab">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Sales Reps</h2>
          <p className="text-sm text-slate-600">Invite reps, view their status, and impersonate their portal.</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="invite-rep-btn">
          <UserPlus size={16} className="mr-2" /> Invite Sales Rep
        </Button>
      </div>

      {showNew && (
        <Card className="p-5 border-2 border-blue-400 bg-blue-50">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-bold text-blue-900">Invite a Sales Rep</h3>
            <button onClick={() => setShowNew(false)} className="text-slate-500">✕</button>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase text-slate-500">Email</label>
              <Input type="email" value={newRep.email} onChange={e => setNewRep({...newRep, email: e.target.value})} className="mt-1" data-testid="new-rep-email" />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-500">Full name</label>
              <Input value={newRep.name} onChange={e => setNewRep({...newRep, name: e.target.value})} className="mt-1" data-testid="new-rep-name-input" />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-500">Territory</label>
              <Input value={newRep.territory} onChange={e => setNewRep({...newRep, territory: e.target.value})} className="mt-1" data-testid="new-rep-territory-input" />
            </div>
          </div>
          <Button onClick={invite} disabled={submitting} className="mt-3 bg-blue-600 hover:bg-blue-700 text-white" data-testid="invite-rep-submit">
            {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send className="mr-2" size={16} />}
            Send invite + copy link
          </Button>
        </Card>
      )}

      {loading ? (
        <Loader2 className="animate-spin text-blue-600 mx-auto" size={32} />
      ) : (
        <Card className="p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-slate-500 border-b">
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Territory</th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">Last login</th>
                <th className="text-left py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reps.map(rep => (
                <tr key={rep.user_id} className="border-b last:border-b-0 hover:bg-slate-50">
                  <td className="py-2 font-medium">{rep.name}</td>
                  <td className="py-2">{rep.email}</td>
                  <td className="py-2">{rep.territory || '—'}</td>
                  <td className="py-2">
                    <Badge className={
                      rep.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                      rep.status === 'invited' ? 'bg-amber-100 text-amber-800' :
                      'bg-slate-200 text-slate-700'
                    }>
                      {rep.status || (rep.is_active ? 'active' : 'inactive')}
                    </Badge>
                  </td>
                  <td className="py-2 text-xs text-slate-500">{rep.last_login ? new Date(rep.last_login).toLocaleDateString() : '—'}</td>
                  <td className="py-2 flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => impersonate(rep)} title="Impersonate — view their portal" data-testid={`impersonate-${rep.user_id}`}>
                      <LogIn size={12} className="mr-1" /> View portal
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(rep)} title={rep.is_active ? 'Deactivate' : 'Activate'}>
                      {rep.is_active ? <PowerOff size={12} /> : <Power size={12} />}
                    </Button>
                  </td>
                </tr>
              ))}
              {reps.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-slate-500">No reps yet. Click <b>Invite Sales Rep</b> to add your first.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default SalesRepsManagementTab;
